import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sitesApi, SiteFilters, CreateSiteRequest } from '@/api/sites';

export function useSites(filters?: SiteFilters) {
  return useQuery({
    queryKey: ['sites', filters],
    queryFn: () => sitesApi.list(filters),
    staleTime: 30 * 1000,
  });
}

export function useSite(id: string) {
  return useQuery({
    queryKey: ['site', id],
    queryFn: () => sitesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteRequest) => sitesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSiteRequest> }) =>
      sitesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['site', id] });
    },
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sitesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useSiteAnalyticalData(
  siteId: string,
  params?: { parameter?: string; start_date?: string; end_date?: string }
) {
  return useQuery({
    queryKey: ['site-analytical', siteId, params],
    queryFn: () => sitesApi.getAnalyticalData(siteId, params),
    enabled: !!siteId,
  });
}

export function useUploadAnalyticalData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, data }: { siteId: string; data: { results: Array<Record<string, unknown>> } }) =>
      sitesApi.uploadAnalyticalData(siteId, data as Parameters<typeof sitesApi.uploadAnalyticalData>[1]),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: ['site-analytical', siteId] });
    },
  });
}

export function useSiteWells(siteId: string) {
  return useQuery({
    queryKey: ['site-wells', siteId],
    queryFn: () => sitesApi.getWells(siteId),
    enabled: !!siteId,
  });
}

export function useSiteReceptors(siteId: string) {
  return useQuery({
    queryKey: ['site-receptors', siteId],
    queryFn: () => sitesApi.getReceptors(siteId),
    enabled: !!siteId,
  });
}
